import { notifications } from '@mantine/notifications';
import { CreateMenuItemPayload, UpdateAvailabilityPayload, UpdateMenuItemPayload } from '@models/payload/menuItem';
import { UpdateMenuItemStatusPayload } from '@models/payload/topping';
import { IError, IErrorResponse } from '@models/response/error';

import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit';
import menuItemService from '@services/menuItems';
import { AxiosError } from 'axios';
import messages from '@shared/constants/messages';
// eslint-disable-next-line import/no-cycle
import { MenuCategory } from '@models/response/category';
import { FoodShiftQuery } from '@models/response/foodShift';
import { MenuItemDto } from '@models/ropolo';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

interface MenuItemState {
  items: (MenuItemDto & { foodShifts: FoodShiftQuery[]; categories: MenuCategory[] })[];
  errors: IError | undefined;
  loading: boolean;
}

const initialState: MenuItemState = {
  items: [],
  errors: undefined,
  loading: false,
};

const deleteMenuItem = createAppAsyncThunk<void, string>('menuItem/deleteMenuItem', async (id) => {
  const response = await menuItemService.removeMenuItem(id);
  notifications.show({
    message: messages.OPERATION_SUBMIT_SUCCESS,
    color: 'green',
  });
  return response;
});

const createMenuItem = createAppAsyncThunk<void, CreateMenuItemPayload>(
  'menuItem/createMenuItem',
  async (input, { rejectWithValue }) => {
    try {
      const response = await menuItemService.createMenuItem(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const updateMenuItem = createAppAsyncThunk<void, UpdateMenuItemPayload>(
  'menuItem/updateMenuItem',
  async (input, { rejectWithValue }) => {
    try {
      const response = await menuItemService.updateMenuItem(input);
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const changeMenuItemAvailable = createAppAsyncThunk<void, UpdateAvailabilityPayload>(
  'menuItem/toggle',
  async (input) => {
    const response = await menuItemService.changeMenuItemToggle(input);
    return response;
  }
);

const changeMenuItemFoodStatus = createAppAsyncThunk<void, UpdateMenuItemStatusPayload>(
  'menuItem/changeMenuItemFoodStatus',
  async (input) => {
    const response = await menuItemService.snappFoodModifyStatus(input);
    return response;
  }
);

const menuItemSlice = createSlice({
  name: 'menuItem',
  initialState,
  reducers: {
    setMenuItems: (state, action: PayloadAction<MenuItemState>) => ({
      ...state,
      items: action.payload.items,
    }),
  },

  extraReducers: (builder: ActionReducerMapBuilder<MenuItemState>) => {
    builder.addCase(createMenuItem.pending, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));
    builder.addCase(createMenuItem.fulfilled, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(createMenuItem.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(updateMenuItem.pending, (state) => ({
      ...state,
      loading: false,
      errors: undefined,
    }));
    builder.addCase(updateMenuItem.fulfilled, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(updateMenuItem.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));

    builder.addCase(deleteMenuItem.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));
    builder.addCase(deleteMenuItem.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(deleteMenuItem.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default menuItemSlice;
export { changeMenuItemAvailable, changeMenuItemFoodStatus, createMenuItem, deleteMenuItem, updateMenuItem };
